// client/src/lessons/les1-motiverende-gespreksvoering/modules/ExtraVerandertaal.js
export const EXTRA_VERANDERTAAL_CONFIG = {
    id: 'extra-verandertaal',
    title: 'Extra Kennis: Verandertaal Herkennen',
    type: 'classification',
    duration: 25,
    
    theory: {
      title: 'Verandertaal herkennen',
      sections: [
        {
          id: 'luisteren',
          title: 'Luisteren naar motivatie',
          content: `Gedragsverandering is zelden eenvoudig: mensen ervaren vaak tegelijkertijd de wens om iets aan te passen én de neiging om alles bij het oude te laten. Deze tegenstrijdige gevoelens noemen we ambivalentie. Aan de ene kant ziet de cliënt de voordelen van verandering, maar aan de andere kant zijn er ook zorgen, twijfels of bezwaren. Beide kanten zijn belangrijk om te herkennen en serieus te nemen.

Wanneer iemand spreekt over de wens om te veranderen of de voordelen daarvan benoemt, noemen we dat verandertaal. Spreekt iemand juist over het behouden van de huidige situatie of de nadelen van verandering? Dan is dat behoudtaal.

Als hulpverlener is het jouw taak om deze signalen op te vangen en samen met de cliënt te verkennen. Zo help je ambivalentie bespreekbaar te maken en te onderzoeken waar de motivatie van de cliënt vandaan kan komen. Later in deze training leer je om verandertaal uit te lokken met gesprekstechnieken, maar eerst meer over verandertaal.`
        },
        {
          id: 'verandertaal',
          title: 'Verandertaal',
          content: `Verandertaal verwijst naar uitspraken van de cliënt die wijzen op motivatie om te veranderen. Hoe vaker en krachtiger iemand hierover spreekt, hoe groter de kans dat hij of zij daadwerkelijk stappen zet. Het is dus ontzettend belangrijk om verandertaal te herkennen in een gesprek.

Binnen motiverende gespreksvoering worden verschillende soorten verandertaal onderscheiden:

**Wensen of verlangens:** wat iemand graag wil bereiken
Voorbeeld: “Ik zou graag weer zonder pijn kunnen lopen.”

**Vermogen:** wat iemand denkt te kunnen
Voorbeeld: “Ik heb in de ochtend genoeg tijd om de oefeningen te doen”

**Redenen:** waarom verandering belangrijk is
Voorbeeld: “Als ik mijn houding verbeter, heb ik minder last van mijn rug.”

**Noodzaak:** Nadelen van het huidige gedrag
Voorbeeld: “Ik moet er nu echt iets aan doen, want dit gaat niet vanzelf over.”`
        },
        {
          id: 'behoudtaal',
          title: 'Behoudtaal',
          content: `Behoudtaal gaat over uitspraken die verandering tegenhouden, zoals het voordeel van het huidige gedrag of weerstand tegen verandering. Het bespreken van behoudtaal hoort óók bij motiverende gespreksvoering: je erkent wat de cliënt lastig vindt zonder daarover te oordelen. Juist door mee te veren bij weerstand blijft het gesprek open.

Voorbeelden van behoudtaal:
“Mijn klachten horen nu eenmaal bij mijn leeftijd.”
“Mijn stem klinkt nu eenmaal zo, daar kan ik weinig aan veranderen.”
“Tja, ik heb er gewoon geen tijd voor om dat elke dag te doen.”`
        },
        {
          id: 'neutraal',
          title: 'Neutrale taal',
          content: `Neutrale taal is niet direct gericht op veranderen of behouden. Het zijn feiten of beschrijvingen zonder een direct verband met motivatie.

Voorbeelden van neutrale taal:
“Ik oefen nu twee keer per week.”
“Mijn energie verschilt per dag.”
“Ik gebruik soms hulpmiddelen bij het koken.”`
        },
        {
          id: 'bronnen',
          title: 'Aanbevolen bronnen',
          content: `Wil je meer weten over verandertaal en behoudtaal? Bekijk dan onderstaande bronnen!

Ondersteun eigen effectiviteit (verandertaal)
https://youtu.be/Wxzrcv50LHM?si=sT69IaQJX374Jdbk
In dit filmpje van het Londfonds zie je in een gesprek een voorbeeld van verandertaal, met name rondom vermogen om te veranderen.
Kijktijd: 3 minuten

Veer mee bij weerstand (behoudtaal)
https://youtu.be/Up5OeG0SAag?si=hrF57pmeyvs0PsTN 

In dit filmpje van het Longfonds zie je in een gesprek een duidelijk voorbeeld van behoudtaal en hoe je daar als hulpverlener mee om kan gaan door mee te veren.
Kijktijd: 3 minuten`
        }
      ]
    },
    
    exercise: {
      title: 'Herkenningsoefening',
      instruction: `In deze trainingssessie oefen je met het herkennen van verandertaal, behoudtaal en neutrale taal. Je gaat een gesprek aan met een virtuele cliënt. Ga een natuurlijk gesprek aan met de cliënt, en geef na elke reactie van de cliënt aan of er sprake is van verandertaal, behoudtaal of neutrale taal. Zodra je vijf keer een goed antwoord hebt gegeven heb je de oefening behaald.`,

      requiredCorrect: 5,
      passingCriteria: 'In een gesimuleerd gesprek in vijf uitspraken herkennen of de cliënt verandertaal, behoudtaal of neutrale taal gebruikt.',
      completionText: `Goed gedaan! Je hebt vijf keer correct aangegeven of de cliënt verandertaal, behoudtaal of neutrale taal gebruikte. Je hebt dus het leerdoel van deze module behaald. Ga verder met de volgende module over open vragen stellen.`,
      
      // Categories for classification
      categories: [
        {
          id: 'verandertaal',
          label: 'Verandertaal',
          color: 'green',
          icon: '🟢',
          description: 'Uitspraken die wijzen op motivatie om te veranderen'
        },
        {
          id: 'behoudtaal', 
          label: 'Behoudtaal',
          color: 'red',
          icon: '🔴',
          description: 'Uitspraken die verandering tegenhouden'
        },
        {
          id: 'neutraal',
          label: 'Neutraal', 
          color: 'blue',
          icon: '🔵',
          description: 'Feiten of beschrijvingen zonder waardeoordeel'
        }
      ]
    },
    
    completionCriteria: {
      type: 'classification_completion',
      requiredCorrect: 10,
      description: 'Correct herkennen van 10 uitspraken als verandertaal, behoudtaal of neutrale taal'
    },
    
    navigation: {
      canSkip: true,
      isOptional: true
    }
  };
  
  export default EXTRA_VERANDERTAAL_CONFIG;